/**
 * LocalStorage utilities for form state persistence
 * Handles saving and restoring form progress
 */

import { FormState, FormAnswers } from '../types/formConfig'

const STORAGE_PREFIX = 'gaia_form_'
const STORAGE_EXPIRY_DAYS = 7 // Forms expire after 7 days

interface StoredFormData extends FormState {
  expiresAt: string
}

/**
 * Save form state to localStorage
 */
export const saveFormState = (formId: string, state: Omit<FormState, 'formId'>): void => {
  try {
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + STORAGE_EXPIRY_DAYS)

    const dataToStore: StoredFormData = {
      formId,
      ...state,
      expiresAt: expiresAt.toISOString()
    }

    localStorage.setItem(`${STORAGE_PREFIX}${formId}`, JSON.stringify(dataToStore))
  } catch (error) {
    console.error('Error saving form state to localStorage:', error)
  }
}

/**
 * Load form state from localStorage
 */
export const loadFormState = (formId: string): FormState | null => {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${formId}`)

    if (!stored) {
      return null
    }

    const data: StoredFormData = JSON.parse(stored)

    // Check if data has expired
    if (new Date(data.expiresAt) < new Date()) {
      clearFormState(formId)
      return null
    }

    // Remove the expiresAt field before returning
    const { expiresAt: _expiresAt, ...formState } = data
    return formState
  } catch (error) {
    console.error('Error loading form state from localStorage:', error)
    return null
  }
}

/**
 * Clear form state from localStorage
 */
export const clearFormState = (formId: string): void => {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${formId}`)
  } catch (error) {
    console.error('Error clearing form state from localStorage:', error)
  }
}

/**
 * Update specific answer in stored form
 */
export const updateFormAnswer = (
  formId: string,
  questionId: string,
  answer: string | string[] | number
): void => {
  try {
    const currentState = loadFormState(formId)

    if (currentState) {
      const updatedAnswers: FormAnswers = {
        ...currentState.answers,
        [questionId]: answer
      }

      saveFormState(formId, {
        ...currentState,
        answers: updatedAnswers,
        timestamp: new Date().toISOString()
      })
    }
  } catch (error) {
    console.error('Error updating form answer:', error)
  }
}

/**
 * Check if a form has saved progress
 */
export const hasFormProgress = (formId: string): boolean => {
  const state = loadFormState(formId)
  return state !== null && Object.keys(state.answers).length > 0
}

/**
 * Get all stored form IDs (for cleanup/debugging)
 */
export const getAllStoredFormIds = (): string[] => {
  try {
    const formIds: string[] = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith(STORAGE_PREFIX)) {
        formIds.push(key.replace(STORAGE_PREFIX, ''))
      }
    }

    return formIds
  } catch (error) {
    console.error('Error getting stored form IDs:', error)
    return []
  }
}

/**
 * Clean up expired forms
 */
export const cleanupExpiredForms = (): void => {
  try {
    const formIds = getAllStoredFormIds()

    formIds.forEach(formId => {
      const state = loadFormState(formId)
      if (!state) {
        // loadFormState already cleared it if expired
        return
      }
    })
  } catch (error) {
    console.error('Error cleaning up expired forms:', error)
  }
}
